import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Lightbulb, TrendingUp, DollarSign, PiggyBank, Target, BookOpen } from "lucide-react";

const funFacts = [
  {
    icon: TrendingUp,
    fact: "The S&P 500 has averaged about 10% annual returns over the past century.",
    category: "Markets"
  },
  {
    icon: DollarSign,
    fact: "If you invested $1,000 in Amazon in 1997, it would be worth over $1.5 million today.",
    category: "Success Stories"
  },
  {
    icon: PiggyBank,
    fact: "The rule of 72: Divide 72 by your interest rate to see how many years it takes to double your money.",
    category: "Money Tip"
  },
  {
    icon: Target,
    fact: "Warren Buffett made 99% of his wealth after turning 50. Patience pays off!",
    category: "Inspiration"
  },
  {
    icon: BookOpen,
    fact: "A TFSA lets Canadians earn investment income completely tax-free.",
    category: "Canadian Tip"
  },
  {
    icon: TrendingUp,
    fact: "Diversification is the only free lunch in investing - Nobel Prize winning advice!",
    category: "Strategy"
  },
  {
    icon: DollarSign,
    fact: "The average millionaire has 7 different income streams.",
    category: "Wealth Building"
  },
  {
    icon: PiggyBank,
    fact: "Compound interest is called the 8th wonder of the world. Start early!",
    category: "Money Tip"
  },
  {
    icon: Target,
    fact: "Index funds beat 90% of actively managed funds over 15+ years.",
    category: "Strategy"
  },
  {
    icon: BookOpen,
    fact: "The best time to invest was 20 years ago. The second best time is now.",
    category: "Inspiration"
  },
  {
    icon: TrendingUp,
    fact: "Dollar-cost averaging removes the stress of timing the market.",
    category: "Strategy"
  },
  {
    icon: DollarSign,
    fact: "Canadian RRSPs give you an immediate tax refund on contributions.",
    category: "Canadian Tip"
  },
  {
    icon: PiggyBank,
    fact: "The 50/30/20 rule: 50% needs, 30% wants, 20% savings.",
    category: "Budgeting"
  },
  {
    icon: Target,
    fact: "ETFs let you own hundreds of stocks with a single purchase.",
    category: "Investing 101"
  },
  {
    icon: BookOpen,
    fact: "Time in the market beats timing the market - every time.",
    category: "Wisdom"
  },
  {
    icon: TrendingUp,
    fact: "The stock market has recovered from every crash in history.",
    category: "Perspective"
  },
  {
    icon: DollarSign,
    fact: "Paying yourself first is the #1 habit of successful savers.",
    category: "Money Tip"
  },
  {
    icon: PiggyBank,
    fact: "A 1% difference in fees can cost you $100,000+ over 30 years.",
    category: "Watch Out"
  }
];

const FunFactsLoader = ({ message = "Loading...", className = "" }) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  useEffect(() => {
    // Randomize starting fact
    setCurrentFactIndex(Math.floor(Math.random() * funFacts.length));
    
    // Change fact every 4 seconds
    const interval = setInterval(() => {
      setCurrentFactIndex(prev => (prev + 1) % funFacts.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const currentFact = funFacts[currentFactIndex];
  const FactIcon = currentFact.icon;

  return (
    <div className={`flex flex-col items-center justify-center p-6 ${className}`}>
      {/* Loading spinner and message */}
      <div className="flex items-center gap-3 mb-8">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        <span className="text-lg font-medium text-slate-700">{message}</span>
      </div>

      {/* Fun fact card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentFactIndex}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="max-w-md w-full"
        >
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-100 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <FactIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                    {currentFact.category}
                  </span>
                </div>
                <p className="text-slate-700 text-sm leading-relaxed">
                  {currentFact.fact}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Progress dots */}
      <div className="flex gap-1.5 mt-6">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
              i === currentFactIndex % 5 ? "bg-emerald-500 w-4" : "bg-slate-300"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default FunFactsLoader;
